import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router";

interface AppHistoryValue {
  canGoBack: boolean;
  canGoForward: boolean;
  back: () => void;
  forward: () => void;
}

const AppHistoryContext = createContext<AppHistoryValue | null>(null);

/**
 * Tracks an internal navigation stack so the top-bar back/forward buttons
 * mirror real browser behaviour and can be disabled when there's nothing to
 * go to. We rely on react-router's NavigationType (PUSH / POP / REPLACE) and
 * compare against the stored stack to detect direction on POP events.
 */
export function AppHistoryProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();

  const stackRef = useRef<string[]>([]);
  const positionRef = useRef(0);

  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);

  useEffect(() => {
    const path = location.pathname + location.search;
    const stack = stackRef.current;
    const pos = positionRef.current;

    if (stack.length === 0) {
      stack.push(path);
      positionRef.current = 0;
    } else if (navType === "PUSH") {
      stack.length = pos + 1;
      stack.push(path);
      positionRef.current = stack.length - 1;
    } else if (navType === "POP") {
      if (pos > 0 && stack[pos - 1] === path) {
        positionRef.current = pos - 1;
      } else if (pos < stack.length - 1 && stack[pos + 1] === path) {
        positionRef.current = pos + 1;
      } else {
        stack.length = 0;
        stack.push(path);
        positionRef.current = 0;
      }
    }

    setCanGoBack(positionRef.current > 0);
    setCanGoForward(positionRef.current < stack.length - 1);
  }, [location, navType]);

  const back = useCallback(() => {
    if (positionRef.current > 0) navigate(-1);
  }, [navigate]);

  const forward = useCallback(() => {
    if (positionRef.current < stackRef.current.length - 1) navigate(1);
  }, [navigate]);

  return (
    <AppHistoryContext.Provider value={{ canGoBack, canGoForward, back, forward }}>
      {children}
    </AppHistoryContext.Provider>
  );
}

export function useAppHistory(): AppHistoryValue {
  const ctx = useContext(AppHistoryContext);
  if (!ctx) {
    return {
      canGoBack: false,
      canGoForward: false,
      back: () => {},
      forward: () => {},
    };
  }
  return ctx;
}
